import React from "react";
import { useHistory } from "react-router-dom";
import { NamedProperty, BreadcrumbItem, Breadcrumb, CodedProperty } from "sprint-common-web";

import { ContentSubtype } from "../../../../../common/types/ContentSubtype";
import { VersionSummary } from "../../../../../common/types/VersionSummary";
import { assetsSearchRoutes, getAssetsSearchPageHref, getTvSeriesAssetsListHref } from "../../../../AssetsRoutes";

interface TvSeriesDetailsPageBreadcrumbsProps {
    tvSeries: NamedProperty;
    season: NamedProperty;
    episode: NamedProperty;
    version: VersionSummary;
}

export const TvSeriesDetailsPageBreadcrumbs: React.FunctionComponent<TvSeriesDetailsPageBreadcrumbsProps> = (
    props: TvSeriesDetailsPageBreadcrumbsProps
) => {
    const history = useHistory();

    const { tvSeries, season, episode, version } = props;

    const goToAssetsSearchPage = () => {
        const href = getAssetsSearchPageHref(ContentSubtype.TV);
        history.push(href);
    };

    const goToTvSeriesAssetsList = (
        tvSeriesId: string,
        seasonId?: string,
        episodeId?: string,
        versionSummary?: VersionSummary
    ) => {
        const versionTypeIds = versionSummary ? [versionSummary.versionType.id] : undefined;
        const distributorIds = versionSummary?.distributors.map((distributor: NamedProperty) => distributor.id);
        const brandingIds = versionSummary?.brandings?.map((branding: NamedProperty) => branding.id);

        const href = getTvSeriesAssetsListHref(tvSeriesId, {
            seasonId,
            episodeId,
            versionTypeIds,
            distributorIds,
            brandingIds
        });
        history.push(href);
    };

    const items: BreadcrumbItem[] = [
        ...assetsSearchRoutes.baseRouteNames.map((name: string) => {
            return { key: name, text: name };
        }),
        {
            key: "search-tv-series-breadcrumb",
            text: "Tv Series",
            onClick: goToAssetsSearchPage
        },
        {
            key: "tv-series-assets-list-breadcrumb",
            text: tvSeries.name,
            onClick: () => goToTvSeriesAssetsList(tvSeries.id)
        },
        {
            key: "tv-series-season-assets-list-breadcrumb",
            text: season.name,
            onClick: () => goToTvSeriesAssetsList(tvSeries.id, season.id)
        },
        {
            key: "tv-series-episode-assets-list-breadcrumb",
            text: episode.name,
            onClick: () => goToTvSeriesAssetsList(tvSeries.id, season.id, episode.id)
        },
        {
            key: "tv-series-episode-version-assets-list-breadcrumb",
            text: getEpisodeVersionText(version),
            onClick: () => goToTvSeriesAssetsList(tvSeries.id, season.id, episode.id, version)
        }
    ];

    return <Breadcrumb items={items} />;
};

const getEpisodeVersionText = (version: VersionSummary): string => {
    const distributorNames = version.distributors.map((distributor: NamedProperty) => distributor.name).join(", ");
    const brandingCodes = version.brandings?.map((branding: CodedProperty) => branding.code).join(", ");

    const episodeVersionTextParts = [distributorNames, version.versionType.name, brandingCodes].filter(
        (part: string | undefined) => !!part
    );

    return episodeVersionTextParts.join(" - ");
};
